import type { GameId, IslandSlot } from '@ezishizi/learning-core';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ContentIndex, MediaRef } from '@ezishizi/content-schema';
import { loadIndex, loadLesson } from '../lib/content.ts';
import {
  ISLE,
  landFraction,
  measureTrail,
  pawPrints,
  polyline,
  SHIZI_SIZE,
  shiziSpot,
  skinFor,
  type Feature,
  type Skin,
  type TrailGeometry,
} from '../lib/islands.ts';
import { getAdventureMode, setAdventureMode } from '../lib/adventure.ts';
import { goToNode, pendingChoice, persistLessonAdvance } from '../lib/pathNav.ts';
import {
  buildPath,
  challengeAttempted,
  currentNode,
  islandStatus,
  lessonToAdvanceTo,
  lessonUnlocked,
  nodeDone,
  nodeStars,
  pathStatuses,
  unlockedOrder,
  type Island,
  type IndexLesson,
  type IndexSet,
  type NodeStatus,
  type PathMode,
  type PathNode,
  type PathSection,
  type StopKind,
} from '../lib/path.ts';
import { foldSession, loadProgress, type ProgressMap } from '../lib/progress.ts';
import { Confetti, Shizi } from '../components/mascot/Shizi.tsx';
import { ModuleIcon } from '../components/icons.tsx';
import { ChestOverlay } from '../components/ChestOverlay.tsx';
import { GameChooser } from '../components/GameChooser.tsx';
import { ChestGlyph, ClapperIcon, StickerBookIcon } from '../components/rewardIcons.tsx';
import { chestPrize, stickerChars } from '../lib/chest.ts';
import {
  buildFilms,
  filmByNumber,
  filmNumberForLesson,
  filmToCelebrate,
  filmUnlocked,
  filmWatched,
  markFilmSeen,
  seenFilms,
  type Film,
} from '../lib/films.ts';
import { markerSession, saveMarkerInBackground } from '../lib/rewardSession.ts';
import { useProfile } from './useProfile.ts';

/**
 * What a games island's stop is called on the map. Short on purpose: the label is one line of
 * 12px text under a disc, with no room to wrap, so "Shell sort by sound" becomes "Shell sort".
 * A slot with no playable format falls back to its slot name from `LABEL`.
 */
const GAME_LABEL: Partial<Record<GameId, string>> = {
  meet_compact: 'Meet',
  meet_ten: 'Meet',
  trio: 'Three',
  forks: 'Boat forks',
  feed: 'Feed Shizi',
  scene: 'Which scene',
  build: 'Build it',
  bubbles: 'Lagoon',
  count: 'Count',
  match_pairs: 'Pairs',
  find_all: 'Find them all',
  sort_meaning: 'Baskets',
  sort_sound: 'Shell sort',
  tone_slide: 'Tone slide',
  tone_paint: 'Tones',
  builder: 'Builder',
  bridge: 'Bridge',
  reveal: 'Reveal',
  stroke_missing: 'One stroke',
  flash: 'Flash',
  stroke_order: 'Stroke order',
};

/** A game stop borrows the icon of the skill stop it stands in for. */
const SLOT_ICON: Record<IslandSlot, StopKind> = {
  meet: 'learn',
  listen: 'listen',
  read: 'read',
  pinyin: 'pinyin',
  write: 'write',
  use: 'play',
};

const LABEL: Record<StopKind, string> = {
  learn: 'New words',
  listen: 'Listen',
  speak: 'Speak',
  play: 'Play',
  read: 'Read',
  write: 'Write',
  review: 'Review',
  pinyin: 'Pinyin',
  game: 'Game',
  word: 'Word',
  challenge: 'Challenge',
  practice: 'Practice',
  reward: 'Reward',
};

/** Where there is no `scrollend` (older Safari), how long a tapped chip or pill keeps its mark
 * with the row no longer scrolling towards it, for a target the row can't bring to the middle.
 * Generous, because a starved main thread can go quiet for seconds in the middle of a scroll that
 * is still on its way. Where `scrollend` exists no clock is used at all: a timer can fire before
 * the scroll it is waiting on has even started. */
const AIM_IDLE_MS = 3000;
const HAS_SCROLLEND = typeof window !== 'undefined' && 'onscrollend' in window;

// Scenery colours. Stops and text use the identity tokens; the land is its own small palette.
/** How far outside the scroller an island keeps animating: one scroller-width of slack either
 * side, so nothing is switched at the exact edge of what the child can see. Pulled out so it can
 * be tuned from a measurement rather than hunted for. */
const SCENERY_MARGIN = '0px 100%';

const SEA_DEEP = '#9ED7E3';
const ROAD = '#F1E3C6';
const ROAD_EDGE = '#DDC79C';
const PAW = '#B07A3C';
const WOOD = '#A8734A';
const WOOD_DEEP = '#8B5A2B';
const INK = 'var(--color-ink)';
const MUTED = 'var(--color-muted)';
const BRAND = 'var(--color-brand)';
const ACCENT = 'var(--color-accent)';
const OK = 'var(--color-ok)';
const LINE = 'var(--color-line)';
const PAPER = 'var(--color-paper)';

const STOP_R = 26;

/** A word's picture by character, for props and stickers. The index carries these per set so
 * the islands never need a lesson bundle (several hundred KB each). */
type MediaByChar = Map<string, { media: MediaRef; picture: boolean }>;

function mediaForSet(set: IndexSet): MediaByChar {
  return new Map(set.pictures.map((p) => [p.char, { media: p.media, picture: p.picture }]));
}

/** A word picture inside the island SVG; tries the thumbnail first, falls back to the full image. */
function WordImage({
  media,
  x,
  y,
  size,
  className,
  style,
  fallback = null,
  onLoad,
}: {
  media: MediaRef;
  x: number;
  y: number;
  size: number;
  className?: string;
  style?: React.CSSProperties;
  /** Drawn instead when neither the thumbnail nor the full image loads. */
  fallback?: ReactNode;
  /** Fires once the picture is on screen (so a placeholder under it can go). */
  onLoad?: () => void;
}) {
  const [src, setSrc] = useState<string | null>(
    media.kind === 'url' ? media.url.replace(/\.webp$/i, '.thumb.webp') : null,
  );
  const [failed, setFailed] = useState(false);
  if (failed) return <>{fallback}</>;
  if (media.kind === 'emoji')
    return (
      <text
        x={x + size / 2}
        y={y + size * 0.78}
        textAnchor="middle"
        fontSize={size * 0.8}
        className={className}
        style={style}
      >
        {media.value}
      </text>
    );
  if (media.kind !== 'url') return null;
  return (
    <image
      href={src ?? media.url}
      x={x}
      y={y}
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={style}
      onLoad={onLoad}
      onError={() => {
        if (src === media.url) setFailed(true);
        else setSrc(media.url);
      }}
    />
  );
}

function StopIcon({
  kind,
  fg,
  reward,
  char,
  slot,
}: {
  kind: StopKind;
  fg: string;
  reward?: 'story' | 'game' | undefined;
  /** The hanzi a word stop shows when it has no picture. */
  char?: string | undefined;
  /** A game stop's slot, which decides which icon it borrows. */
  slot?: IslandSlot | undefined;
}) {
  const stroke = {
    stroke: fg,
    strokeWidth: 3,
    fill: 'none',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  // A game stop is drawn as the skill it stands in for: the child reads the row of discs as
  // "meet, hear, read, sound, write, use" whatever formats today's island picked.
  if (kind === 'game') return <StopIcon kind={slot ? SLOT_ICON[slot] : 'play'} fg={fg} />;
  switch (kind) {
    case 'learn':
      return (
        <path
          d="M-13 -9 h10 q3 0 3 3 v15 q-3 -3 -6 -3 h-7 z M13 -9 h-10 q-3 0 -3 3 v15 q3 -3 6 -3 h7 z"
          fill={fg}
        />
      );
    case 'listen':
      return (
        <>
          <path d="M-12 -5 h6 l8 -7 v24 l-8 -7 h-6 z" fill={fg} />
          <path d="M7 -5 q5 5 0 10" {...stroke} />
          <path d="M11 -10 q9 10 0 20" {...stroke} />
        </>
      );
    case 'speak':
      return (
        <>
          <rect x={-6} y={-14} width={12} height={20} rx={6} fill={fg} />
          <path d="M-11 0 q0 11 11 11 q11 0 11 -11 M0 11 v5" {...stroke} />
        </>
      );
    case 'read':
      return (
        <text y={9} textAnchor="middle" fontSize={26} className="hanzi" fill={fg}>
          字
        </text>
      );
    case 'write':
      return (
        <>
          <path d="M-12 12 L8 -8" {...stroke} />
          <path d="M8 -8 l6 -6 l3 3 l-6 6 z" fill={fg} />
          <path d="M-12 12 l-3 5 l6 -2 z" fill={fg} />
        </>
      );
    case 'review':
      return (
        <>
          <path d="M-10 -2 a10 10 0 0 1 17 -7" {...stroke} />
          <path d="M10 2 a10 10 0 0 1 -17 7" {...stroke} />
          <path d="M7 -13 l1 5 l-5 0 z M-7 13 l-1 -5 l5 0 z" fill={fg} />
        </>
      );
    case 'pinyin':
      return (
        <text y={8} textAnchor="middle" fontSize={26} fontWeight={800} fill={fg}>
          ā
        </text>
      );
    case 'play':
      return <path d="M-9 -12 L13 0 L-9 12 Z" fill={fg} />;
    case 'word':
      return (
        <text y={9} textAnchor="middle" fontSize={26} className="hanzi" fill={fg}>
          {char}
        </text>
      );
    case 'challenge':
      return <path d="M-10 -13 v26 M-10 -13 h20 l-5 6 5 6 h-20" {...stroke} />;
    case 'practice':
      return (
        <text y={9} textAnchor="middle" fontSize={22} className="hanzi" fill={fg}>
          文
        </text>
      );
    case 'reward':
      return reward === 'game' ? <ChestGlyph fg={fg} /> : <path d={star(0, 0, 13, 6)} fill={fg} />;
  }
}

function star(cx: number, cy: number, R: number, r: number): string {
  let d = '';
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 ? r : R;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    d += `${i ? 'L' : 'M'}${(cx + rad * Math.cos(a)).toFixed(1)} ${(cy + rad * Math.sin(a)).toFixed(1)} `;
  }
  return d + 'Z';
}

function Stars({ n, y }: { n: number; y: number }) {
  return (
    <g aria-hidden>
      {[0, 1, 2].map((i) => (
        <path
          key={i}
          d={star((i - 1) * 14, y, 6, 2.8)}
          fill={i < n ? ACCENT : 'rgba(30,36,51,0.15)'}
        />
      ))}
    </g>
  );
}

/** A plain chevron, used by the two big arrows either side of the island scroller. Drawn inline
 * rather than borrowed from `icons.tsx` because every icon there is a stroked 24x24 glyph tuned
 * for a small button, not the thick, chunky mark a round h-14 button wants. */
function ChevronIcon({ dir, className }: { dir: 'left' | 'right'; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The big round arrows either side of the island scroller: a mouse or a thumb that has never
 * swiped a trackpad still needs a way across. They float over the scroller in a pointer-events
 * layer of their own (see the `isles` wrapper) so the island underneath stays fully tappable.
 *
 * They ride low rather than at mid-height, which is where the eye would put them: an island is
 * nearly as wide as the screen, so a centred arrow lands on the pier at its edge and swallows
 * the taps meant for the reward disc standing there. The sea below the island is the one band
 * with nothing in it at any screen shape, and low down is where a thumb holding a tablet
 * already is. The corners themselves are left alone: `?debug=1` parks the diagnostics bug in
 * one and its console in the other.
 */
function IsleArrow({ dir, onGo }: { dir: 'prev' | 'next'; onGo: () => void }) {
  return (
    <button
      type="button"
      className={`pointer-events-auto absolute bottom-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-brand shadow-lg landscape:h-16 landscape:w-16 ${
        dir === 'prev' ? 'left-16' : 'right-16'
      }`}
      aria-label={dir === 'prev' ? 'Previous island' : 'Next island'}
      data-testid={dir === 'prev' ? 'isles-prev' : 'isles-next'}
      onClick={onGo}
    >
      <ChevronIcon
        dir={dir === 'prev' ? 'left' : 'right'}
        className="h-7 w-7 landscape:h-8 landscape:w-8"
      />
    </button>
  );
}

function LockIcon({ fg }: { fg: string }) {
  return (
    <>
      <rect x={-10} y={-3} width={20} height={15} rx={3} fill={fg} />
      <path d="M-6 -3 v-5 a6 6 0 0 1 12 0 v5" stroke={fg} strokeWidth={3} fill="none" />
    </>
  );
}

function Stop({
  node,
  status,
  stars,
  attempted,
  picture,
  x,
  y,
  onGo,
}: {
  node: PathNode;
  status: NodeStatus;
  stars: number;
  attempted: boolean;
  /** A word stop's illustration, drawn in the disc; the hanzi stands in when there is none. */
  picture?: MediaRef | undefined;
  x: number;
  y: number;
  onGo: () => void;
}) {
  const isCur = status === 'current';
  const locked = status === 'locked';
  const done = status === 'done';
  const reward = node.kind === 'reward';
  const bg = done ? OK : isCur ? BRAND : locked ? LINE : reward ? ACCENT : '#ffffff';
  const fg = done || isCur || (reward && !locked) ? '#ffffff' : locked ? '#a5a9b3' : BRAND;
  const label =
    node.kind === 'reward'
      ? node.reward.type === 'story'
        ? 'Story'
        : 'Chest'
      : node.kind === 'word'
        ? node.char
        : node.kind === 'game'
          ? node.alt
            ? 'Pick a game'
            : ((node.game && GAME_LABEL[node.game]) ?? LABEL[SLOT_ICON[node.slot]])
          : LABEL[node.kind];
  const clipId = `stop-${node.id}-clip`;
  const talk = isCur || status === 'open';
  // A word stop shows its hanzi until the picture has loaded, so the disc is never blank.
  const [pictureShown, setPictureShown] = useState(false);
  // Tapping a locked reward: the padlock wiggles and says why, nothing opens.
  const [nudge, setNudge] = useState(0);
  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => setNudge(0), 1800);
    return () => clearTimeout(t);
  }, [nudge]);
  const activate = () => {
    if (locked) setNudge(Date.now());
    else onGo();
  };
  return (
    <g
      transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}
      data-testid={`stop-${node.id}`}
      data-status={status}
    >
      {/* The breathing "current stop" ring lives outside the tappable button: its radius grows
          and shrinks every 2.4s (isle-pulse), and an animated descendant keeps growing the
          button's own bounding box for as long as it plays. A real tap doesn't care, but
          Playwright's click() waits for its target to stop moving before it will click, and a
          ring that never stops moving made that wait spin for several seconds at a time —
          occasionally past the whole e2e-webkit run's budget. Nothing the child taps should
          move (see the ambient-motion comment in styles.css); this ring is scenery, not button. */}
      {isCur && (
        <>
          <circle r={STOP_R + 12} fill={BRAND} opacity={0.22} style={{ pointerEvents: 'none' }} />
          <circle
            className="isle-pulse"
            r={STOP_R + 12}
            fill="none"
            stroke={BRAND}
            strokeWidth={4}
            style={{ pointerEvents: 'none' }}
          />
        </>
      )}
      <g
        role="button"
        tabIndex={0}
        aria-label={locked ? `${label}, locked until the island is finished` : label}
        onClick={activate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate();
          }
        }}
        className={nudge ? 'isle-nudge' : undefined}
        key={nudge}
      >
        {/* Keeps the ring's footprint tappable now that the ring itself sits outside the button.
            Static, so the button's bounds still never move. */}
        {isCur && <circle r={STOP_R + 12} fill="transparent" />}
        {reward && !locked && !done && !isCur && (
          <circle
            r={STOP_R + 7}
            fill="none"
            stroke={ACCENT}
            strokeWidth={3}
            strokeDasharray="6 6"
          />
        )}
        {!locked && <circle cy={4} r={STOP_R} fill="rgba(30,36,51,0.14)" />}
        <circle className="stop-disc" r={STOP_R} fill={bg} />
        {locked ? (
          <LockIcon fg={fg} />
        ) : node.kind === 'word' && picture ? (
          <>
            <clipPath id={clipId}>
              <circle r={STOP_R - 4} />
            </clipPath>
            <circle r={STOP_R - 4} fill="#fff" />
            {!pictureShown && (
              <text y={9} textAnchor="middle" fontSize={26} className="hanzi" fill={INK}>
                {node.char}
              </text>
            )}
            <g clipPath={`url(#${clipId})`} opacity={done ? 0.85 : 1}>
              <WordImage
                media={picture}
                x={-(STOP_R - 4)}
                y={-(STOP_R - 4)}
                size={(STOP_R - 4) * 2}
                onLoad={() => setPictureShown(true)}
                fallback={
                  <text y={9} textAnchor="middle" fontSize={26} className="hanzi" fill={INK}>
                    {node.char}
                  </text>
                }
              />
            </g>
            {done && (
              <g transform={`translate(${STOP_R - 8} ${-STOP_R + 8})`}>
                <circle r={8} fill={OK} />
                <path d="M-3.5 0 l2.5 2.5 l4.5 -5" stroke="#fff" strokeWidth={2} fill="none" />
              </g>
            )}
          </>
        ) : (
          <StopIcon
            kind={node.kind}
            fg={fg}
            reward={reward ? node.reward.type : undefined}
            char={node.kind === 'word' ? node.char : undefined}
            slot={node.kind === 'game' ? node.slot : undefined}
          />
        )}
        {/* A stop that offers two games wears a small "2" so it reads as a choice. */}
        {node.kind === 'game' && node.alt && !locked && !done && (
          <g transform={`translate(${STOP_R - 6} ${-STOP_R + 6})`} data-testid="choice-badge">
            <circle r={9} fill={ACCENT} stroke="#fff" strokeWidth={2} />
            <text y={4} textAnchor="middle" fontSize={11} fontWeight={800} fill="#fff">
              2
            </text>
          </g>
        )}
      </g>
      {talk && (
        <text
          y={STOP_R + 18}
          textAnchor="middle"
          fontSize={node.kind === 'word' ? 16 : 12}
          fontWeight={700}
          fill={INK}
          className={node.kind === 'word' ? 'hanzi' : undefined}
        >
          {attempted ? 'Try again' : label}
        </text>
      )}
      {(done || attempted) && <Stars n={stars} y={STOP_R + (attempted ? 32 : 14)} />}
      {nudge > 0 && (
        <g transform={`translate(0 ${-STOP_R - 34})`} className="pop" data-testid="lock-nudge">
          <rect x={-92} y={-16} width={184} height={32} rx={16} fill={INK} opacity={0.92} />
          <g transform="translate(-74 -1) scale(0.7)">
            <LockIcon fg="#fff" />
          </g>
          <text x={8} y={5} textAnchor="middle" fontSize={12} fontWeight={700} fill="#fff">
            Finish the island first
          </text>
        </g>
      )}
      {isCur && (
        <g transform={`translate(0 ${STOP_R + 36})`}>
          <rect x={-30} y={-10} width={60} height={20} rx={10} fill={BRAND} />
          <text
            y={4}
            textAnchor="middle"
            fontSize={10.5}
            fontWeight={800}
            fill="#fff"
            letterSpacing={1.6}
          >
            START
          </text>
        </g>
      )}
    </g>
  );
}

function FeatureArt({ feature }: { feature: Feature }) {
  switch (feature) {
    case 'river':
      return (
        <>
          <path
            d="M560 40 C520 90 470 120 430 180 C390 240 330 300 250 346 L330 348 C400 300 460 240 500 180 C540 120 590 90 590 60 Z"
            fill={SEA_DEEP}
          />
          <path
            d="M565 60 C530 100 480 130 445 185 C410 240 350 300 290 340"
            stroke="#DDF1F4"
            strokeWidth={5}
            fill="none"
            strokeLinecap="round"
            className="isle-shimmer"
          />
        </>
      );
    case 'sun':
      return (
        <g className="isle-glow">
          <circle cx={520} cy={70} r={30} fill="#FBD98F" />
          <circle cx={520} cy={70} r={20} fill="#F2A93B" />
        </g>
      );
    case 'road':
      return (
        <path
          d="M60 230 C160 210 240 60 380 60 C460 60 520 120 580 130"
          stroke="#E4D5B8"
          strokeWidth={16}
          fill="none"
          strokeLinecap="round"
          opacity={0.8}
        />
      );
    case 'pond':
      return <ellipse cx={430} cy={300} rx={60} ry={24} fill={SEA_DEEP} />;
    default:
      return null;
  }
}

/** What an island at the end of a film-unlocking lesson shows beside its reward. */
interface FilmTicketInfo {
  n: number;
  unlocked: boolean;
  watched: boolean;
}

/**
 * The film ticket floating past the lesson's last reward: dim and padlocked until the lesson is
 * complete, then a gold ticket that opens the film (and a tick once it has been watched). Sits in
 * the sea above the pier, clear of the gate and the reward disc.
 */
function FilmTicket({
  info,
  x,
  y,
  onOpen,
}: {
  info: FilmTicketInfo;
  x: number;
  y: number;
  onOpen: (n: number) => void;
}) {
  const [nudge, setNudge] = useState(0);
  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => setNudge(0), 2200);
    return () => clearTimeout(t);
  }, [nudge]);
  const { unlocked, watched } = info;
  const activate = () => {
    if (unlocked) onOpen(info.n);
    else setNudge(Date.now());
  };
  return (
    <g
      transform={`translate(${x} ${y})`}
      data-testid={`film-ticket-${info.n}`}
      data-state={unlocked ? (watched ? 'watched' : 'ready') : 'locked'}
    >
      <g
        role="button"
        tabIndex={0}
        aria-label={
          unlocked
            ? `Film ${info.n}, ${watched ? 'watch again' : 'ready to watch'}`
            : `Film ${info.n}, locked until the lesson is finished`
        }
        onClick={activate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate();
          }
        }}
      >
        <rect x={-34} y={-30} width={68} height={60} fill="transparent" />
        <g className={unlocked && !watched ? 'ticket-wiggle' : undefined}>
          <rect
            x={-28}
            y={-17}
            width={56}
            height={34}
            rx={7}
            fill="rgba(30,36,51,0.14)"
            transform="translate(0 3)"
          />
          <rect x={-28} y={-17} width={56} height={34} rx={7} fill={unlocked ? ACCENT : LINE} />
          <circle cx={-28} cy={0} r={5} fill={SEA_DEEP} />
          <circle cx={28} cy={0} r={5} fill={SEA_DEEP} />
          <path
            d="M-14 -13 v26"
            stroke={unlocked ? '#fff' : '#a5a9b3'}
            strokeWidth={2}
            strokeDasharray="3 4"
            opacity={0.8}
          />
          {unlocked ? (
            <g
              transform="translate(8 0) scale(0.62) translate(-12 -12)"
              stroke="#fff"
              fill="none"
              strokeWidth={2.4}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="9" width="18" height="12" rx="2" />
              <path d="M3 9l2-5 4 1-2 4M9 5l4 1-2 4M13 6l4 1-2 4M17 7l3 1-1 3" />
            </g>
          ) : (
            <g transform="translate(8 -1) scale(0.7)">
              <LockIcon fg="#a5a9b3" />
            </g>
          )}
          <text
            x={-21}
            y={4}
            fontSize={9}
            fontWeight={800}
            fill={unlocked ? '#fff' : '#a5a9b3'}
            letterSpacing={0.5}
          >
            {info.n}
          </text>
        </g>
        {watched && (
          <g transform="translate(26 -17)">
            <circle r={8} fill={OK} />
            <path d="M-3.5 0 l2.5 2.5 l4.5 -5" stroke="#fff" strokeWidth={2} fill="none" />
          </g>
        )}
      </g>
      <text y={34} textAnchor="middle" fontSize={12} fontWeight={700} fill={INK}>
        Film
      </text>
      {nudge > 0 && (
        <g transform="translate(-90 -44)" className="pop" key={nudge} data-testid="film-nudge">
          <rect x={-100} y={-16} width={200} height={32} rx={16} fill={INK} opacity={0.92} />
          <text y={5} textAnchor="middle" fontSize={12} fontWeight={700} fill="#fff">
            Finish this lesson to unlock it
          </text>
        </g>
      )}
    </g>
  );
}

function Signpost({
  x,
  y,
  top,
  main,
  sub,
  mode,
  width,
  testId,
}: {
  x: number;
  y: number;
  top: string;
  main: ReactNode;
  sub?: string | undefined;
  /** folded = locked lesson (padlock); ahead = unlocked lesson the child hasn't opened yet. */
  mode: 'current' | 'done' | 'folded' | 'ahead';
  width: number;
  testId?: string | undefined;
}) {
  const h = 54;
  const folded = mode === 'folded';
  const dim = folded || mode === 'ahead';
  return (
    <g transform={`translate(${x} ${y})`} data-testid={testId}>
      <rect x={-5} y={h / 2} width={10} height={30} rx={3} fill={WOOD} />
      <rect
        x={-width / 2}
        y={-h / 2 + 4}
        width={width}
        height={h}
        rx={14}
        fill="rgba(30,36,51,0.12)"
      />
      <rect
        x={-width / 2}
        y={-h / 2}
        width={width}
        height={h}
        rx={14}
        fill={mode === 'current' ? 'var(--color-brand-soft)' : '#ffffff'}
        stroke={dim ? LINE : 'none'}
      />
      {folded && (
        <g transform={`translate(${-width / 2 + 24} -1)`}>
          <LockIcon fg="#a5a9b3" />
        </g>
      )}
      <text
        x={folded ? 12 : 0}
        y={-9}
        textAnchor="middle"
        fontSize={10.5}
        fontWeight={800}
        letterSpacing={1.4}
        fill={dim ? MUTED : BRAND}
      >
        {top}
      </text>
      <text
        x={folded ? 12 : sub ? -6 : 0}
        y={15}
        textAnchor={sub ? 'end' : 'middle'}
        fontSize={21}
        className="hanzi"
        fill={dim ? MUTED : INK}
      >
        {main}
      </text>
      {sub && (
        <text x={4} y={14} textAnchor="start" fontSize={12} fontWeight={700} fill={MUTED}>
          {sub}
        </text>
      )}
    </g>
  );
}

function Pier({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}>
      <rect x={-10} y={-30} width={140} height={60} rx={6} fill={WOOD} />
      {Array.from({ length: 10 }, (_, i) => (
        <rect
          key={i}
          x={-2 + i * 13}
          y={-30}
          width={3}
          height={60}
          fill={WOOD_DEEP}
          opacity={0.45}
        />
      ))}
      {[
        [-4, -34],
        [-4, 26],
        [118, -34],
        [118, 26],
      ].map(([dx, dy], i) => (
        <rect key={i} x={dx} y={dy} width={20} height={8} rx={2} fill={WOOD_DEEP} />
      ))}
    </g>
  );
}

function Gate({ x, y, open }: { x: number; y: number; open: boolean }) {
  return (
    <g transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}>
      <rect x={-44} y={-62} width={10} height={100} rx={3} fill={WOOD_DEEP} />
      <rect x={34} y={-62} width={10} height={100} rx={3} fill={WOOD_DEEP} />
      <path
        d="M-44 -56 q44 -34 88 0"
        stroke={WOOD_DEEP}
        strokeWidth={10}
        fill="none"
        strokeLinecap="round"
      />
      <rect x={-1.5} y={-108} width={3} height={34} fill={WOOD_DEEP} />
      <g className="isle-flap">
        <path d="M1.5 -108 h20 l-5 6 5 6 h-20 z" fill={open ? OK : ACCENT} />
      </g>
    </g>
  );
}

function Boat({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} className="isle-bob">
      <path d="M-34 0 q34 26 68 0 l-8 -16 h-52 z" fill={WOOD} />
      <rect x={-30} y={-18} width={60} height={6} rx={3} fill={WOOD_DEEP} />
      <path d="M-44 8 q44 14 88 0" stroke="#fff" strokeWidth={3} fill="none" opacity={0.8} />
    </g>
  );
}

function Cloud({ x, y, s, delay }: { x: number; y: number; s: number; delay: string }) {
  return (
    <g className="isle-drift" style={{ animationDelay: delay }} opacity={0.95}>
      <ellipse cx={x} cy={y} rx={26 * s} ry={11 * s} fill="#fff" />
      <circle cx={x - 8 * s} cy={y - 6 * s} r={11 * s} fill="#fff" />
      <circle cx={x + 9 * s} cy={y - 4 * s} r={9 * s} fill="#fff" />
    </g>
  );
}

/** The island body shared by every set and by folded lessons: sea, rim, ground, feature. */
function IslandBody({ skin, children }: { skin: Skin; children?: ReactNode }) {
  return (
    <>
      <path d={ISLE.outline} fill="none" stroke="#fff" strokeWidth={26} opacity={0.35} />
      <path d={ISLE.outline} fill={skin.rim} />
      <path
        d={ISLE.outline}
        transform="translate(300 180) scale(0.945) translate(-300 -180)"
        fill={skin.ground}
      />
      <FeatureArt feature={skin.feature} />
      {children}
    </>
  );
}

function Ripples() {
  return (
    <g aria-hidden>
      {[
        [-60, 20],
        [640, 40],
        [-80, 400],
        [660, 380],
        [300, -30],
      ].map(([x, y], i) => (
        <path
          key={i}
          d={`M${x} ${y} q12 -6 24 0 t24 0`}
          stroke="#fff"
          strokeWidth={2.5}
          fill="none"
          strokeLinecap="round"
          className="isle-ripple"
          style={{ animationDelay: `${-i * 1.3}s` }}
        />
      ))}
    </g>
  );
}

const svgProps = {
  viewBox: `${ISLE.view.x} ${ISLE.view.y} ${ISLE.view.w} ${ISLE.view.h}`,
  className: 'isle',
};

/**
 * A whole lesson drawn as one misted island with its sign: either unlocked but not started yet
 * (a tap opens it), or beyond the unlock frontier (locked: a tap only says to ask a parent).
 */
// Memoized: a chip tap or the scroll passing an island only ever changes which lesson/set is
// marked, and without this every folded island re-rendered its SVG for that too, adding to the
// same long task the scroll was trying to start.
const FoldedIsland = memo(function FoldedIsland({
  lesson,
  geo,
  locked,
  onOpen,
}: {
  lesson: IndexLesson;
  geo: TrailGeometry;
  locked: boolean;
  onOpen: (id: string) => void;
}) {
  const skin = skinFor(lesson);
  const L = geo.length;
  const [nudge, setNudge] = useState(0);
  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => setNudge(0), 2200);
    return () => clearTimeout(t);
  }, [nudge]);
  const activate = () => {
    if (locked) setNudge(Date.now());
    else onOpen(lesson.id);
  };
  return (
    <svg
      {...svgProps}
      role="button"
      tabIndex={0}
      aria-label={
        locked
          ? `Lesson ${lesson.order}, locked. Ask a parent to unlock it`
          : `Lesson ${lesson.order}, tap to open`
      }
      data-testid={`folded-${lesson.id}`}
      data-locked={locked ? 'true' : 'false'}
      data-lesson={lesson.id}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
    >
      <Ripples />
      <IslandBody skin={skin} />
      <polyline
        points={polyline(geo, 0, L)}
        stroke="#C9B892"
        strokeWidth={3}
        fill="none"
        strokeDasharray="9 11"
        opacity={0.55}
      />
      <path d={ISLE.outline} fill={PAPER} opacity={0.62} />
      <Cloud x={160} y={150} s={1.1} delay="0s" />
      <Cloud x={330} y={210} s={1.3} delay="-5s" />
      <Cloud x={480} y={130} s={0.9} delay="-9s" />
      <Signpost
        x={ISLE.sign[0]}
        y={ISLE.sign[1]}
        top={`LESSON ${lesson.order}`}
        main={lesson.title?.zh ?? ''}
        sub={lesson.title?.en}
        mode={locked ? 'folded' : 'ahead'}
        width={300}
        testId={`path-lesson-${lesson.id}`}
      />
      {!locked && (
        <g transform={`translate(${ISLE.sign[0]} ${ISLE.sign[1] + 62})`}>
          <rect x={-52} y={-11} width={104} height={22} rx={11} fill={BRAND} />
          <text
            y={4}
            textAnchor="middle"
            fontSize={10.5}
            fontWeight={800}
            fill="#fff"
            letterSpacing={1.6}
          >
            TAP TO OPEN
          </text>
        </g>
      )}
      {nudge > 0 && (
        <g
          transform={`translate(${ISLE.sign[0]} ${ISLE.sign[1] + 70})`}
          className="pop"
          key={nudge}
          data-testid="unlock-nudge"
        >
          <rect x={-128} y={-16} width={256} height={32} rx={16} fill={INK} opacity={0.92} />
          <g transform="translate(-110 -1) scale(0.7)">
            <LockIcon fg="#fff" />
          </g>
          <text x={8} y={5} textAnchor="middle" fontSize={12} fontWeight={700} fill="#fff">
            Ask a parent to unlock lesson {lesson.order}
          </text>
        </g>
      )}
    </svg>
  );
});

// Memoized for the same reason as FoldedIsland above: a chip tap or the scroll passing an
// island only marks a different chip, and unmemoized this re-rendered every island's SVG
// before the scroll could even start.
const IslandView = memo(function IslandView({
  island,
  statuses,
  progress,
  geo,
  first,
  onGo,
  ticket,
  onTicket,
}: {
  island: Island;
  statuses: Map<string, NodeStatus>;
  progress: ProgressMap;
  geo: TrailGeometry;
  /** First island of its lesson: carries the lesson sign. */
  first: boolean;
  onGo: (node: PathNode) => void;
  /** The story film this island's lesson unlocks, on the lesson's last island. */
  ticket?: FilmTicketInfo | undefined;
  onTicket: (n: number) => void;
}) {
  const { lesson, set } = island;
  const skin = skinFor(lesson);
  const L = geo.length;
  const media = useMemo(() => mediaForSet(set), [set]);
  const status = islandStatus(island, statuses);

  const land = island.nodes.filter(
    (n) => n.kind !== 'challenge' && n.kind !== 'practice' && n.kind !== 'reward',
  );
  // The pier gate is the Challenge everywhere except a P3+ island whose main event is its
  // practice set instead (spec §8 decision 2): same spot on the map either way.
  const gate = island.nodes.find((n) => n.kind === 'challenge' || n.kind === 'practice');
  const reward = island.nodes.find((n) => n.kind === 'reward');
  const placed = land.map((n, i) => ({ n, len: landFraction(i, land.length) * L }));
  if (gate) placed.push({ n: gate, len: ISLE.gate * L });
  if (reward) placed.push({ n: reward, len: ISLE.reward * L });
  const st = (n: PathNode) => statuses.get(n.id) ?? 'locked';
  const walked = (a: PathNode, b: PathNode) =>
    st(a) === 'done' && (st(b) === 'done' || st(b) === 'current');

  // Road stretches: paw prints where Shizi has walked, dashes ahead.
  const stretches: Array<{ from: number; to: number; walked: boolean; faint?: boolean }> = [];
  const firstNode = placed[0]!.n;
  const arrived = st(firstNode) === 'done' || st(firstNode) === 'current';
  stretches.push({ from: ISLE.shore * L - 20, to: placed[0]!.len, walked: arrived });
  for (let i = 0; i < placed.length - 1; i++)
    stretches.push({
      from: placed[i]!.len,
      to: placed[i + 1]!.len,
      walked: walked(placed[i]!.n, placed[i + 1]!.n),
    });
  const last = placed[placed.length - 1]!;
  stretches.push({ from: last.len, to: L, walked: st(last.n) === 'done', faint: true });

  const chars = [...set.chars];
  const wordStops = land.filter((n) => n.kind === 'word');
  // Stickers: in the words layout, the picture of each finished word; otherwise one word
  // picture per finished skill stop.
  const stickers = wordStops.length
    ? wordStops
        .filter((n) => nodeDone(n, progress))
        .map((n) => (n.kind === 'word' ? n.char : ''))
        .slice(0, ISLE.pads.length)
    : chars.slice(0, Math.min(land.filter((n) => nodeDone(n, progress)).length, ISLE.pads.length));
  const propChars = chars.filter((c) => media.get(c)?.picture).slice(0, ISLE.spots.length);
  const pierP = geo.at(ISLE.pier * L);
  const cur = placed.find((p) => st(p.n) === 'current');
  const curP = cur ? geo.at(cur.len) : null;
  // Shizi stands beside the current stop, clear of the others: the stop the child just finished
  // must stay visible and tappable for a replay.
  const shizi = curP
    ? shiziSpot(
        curP,
        placed
          .filter((p) => p !== cur)
          .map((p) => geo.at(p.len))
          .concat([pierP]),
        SHIZI_SIZE,
        placed.map((p) => geo.at(p.len)),
        first,
      )
    : null;

  return (
    <svg
      {...svgProps}
      role="group"
      aria-label={`Lesson ${lesson.order} set ${set.order}`}
      data-testid={`island-${set.id}`}
      data-status={status}
    >
      <Ripples />
      {/* the boat route in from the previous pier */}
      <polyline
        points={polyline(geo, 0, ISLE.shore * L)}
        stroke="#fff"
        strokeWidth={4}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={arrived ? '2 14' : '6 12'}
        opacity={arrived ? 0.95 : 0.6}
      />
      <IslandBody skin={skin}>
        {propChars.map((c, i) => {
          const [sx, sy] = ISLE.spots[i]!;
          return (
            <WordImage
              key={c}
              media={media.get(c)!.media}
              x={sx - 36}
              y={sy - 70}
              size={72}
              className="isle-bob"
              style={{ animationDelay: `${-i * 1.1}s`, animationDuration: `${3.4 + i * 0.6}s` }}
            />
          );
        })}
      </IslandBody>

      {/* road */}
      <polyline
        points={polyline(geo, ISLE.shore * L, ISLE.pier * L + 30, 5)}
        stroke={ROAD_EDGE}
        strokeWidth={26}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={polyline(geo, ISLE.shore * L, ISLE.pier * L + 30, 5)}
        stroke={ROAD}
        strokeWidth={20}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {stretches.map((s, i) =>
        s.walked ? (
          <g key={i} fill={PAW} opacity={0.85}>
            {pawPrints(geo, s.from, s.to).map((p, j) => (
              <g
                key={j}
                transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) rotate(${p.angle.toFixed(1)})`}
              >
                <ellipse cy={2} rx={3.6} ry={3} />
                <circle cx={-3.8} cy={-3.4} r={1.4} />
                <circle cx={-1.3} cy={-5.6} r={1.4} />
                <circle cx={1.3} cy={-5.6} r={1.4} />
                <circle cx={3.8} cy={-3.4} r={1.4} />
              </g>
            ))}
          </g>
        ) : (
          <polyline
            key={i}
            points={polyline(geo, s.from, s.to)}
            stroke="#C9B892"
            strokeWidth={3}
            fill="none"
            strokeDasharray="9 11"
            strokeLinecap="round"
            opacity={s.faint ? 0.6 : 0.9}
          />
        ),
      )}

      <Pier x={pierP.x} y={pierP.y} />
      {gate && (
        <Gate x={geo.at(ISLE.gate * L).x} y={geo.at(ISLE.gate * L).y} open={st(gate) === 'done'} />
      )}
      {status === 'current' && <Boat x={-40} y={262} />}

      {/* stickers: one word picture per finished stop */}
      {stickers.map((c, j) => {
        const [px, py] = ISLE.pads[j]!;
        const m = media.get(c);
        const clipId = `${set.id}-pad-${j}`;
        return (
          <g key={c} transform={`translate(${px} ${py}) rotate(${j % 2 ? 8 : -7})`}>
            <circle cy={2} r={19} fill="rgba(30,36,51,0.10)" />
            <circle r={19} fill="#fff" stroke={LINE} strokeWidth={1.5} />
            {m ? (
              <>
                <clipPath id={clipId}>
                  <circle r={16} />
                </clipPath>
                <g clipPath={`url(#${clipId})`}>
                  <WordImage
                    media={m.media}
                    x={-16}
                    y={-16}
                    size={32}
                    fallback={
                      <text y={7} textAnchor="middle" fontSize={20} className="hanzi" fill={INK}>
                        {c}
                      </text>
                    }
                  />
                </g>
              </>
            ) : (
              <text y={7} textAnchor="middle" fontSize={20} className="hanzi" fill={INK}>
                {c}
              </text>
            )}
          </g>
        );
      })}

      {first ? (
        <Signpost
          x={ISLE.sign[0] + 32}
          y={ISLE.sign[1]}
          top={`LESSON ${lesson.order} · SET ${set.order}`}
          main={lesson.title?.zh ?? set.chars}
          sub={lesson.title?.en}
          mode={status === 'current' ? 'current' : 'done'}
          width={300}
          testId={`path-lesson-${lesson.id}`}
        />
      ) : (
        <Signpost
          x={ISLE.sign[0]}
          y={ISLE.sign[1]}
          top={`LESSON ${lesson.order} · SET ${set.order}`}
          main={set.chars}
          mode={status === 'current' ? 'current' : 'done'}
          width={236}
        />
      )}

      {placed.map(({ n, len }) => {
        const p = geo.at(len);
        return (
          <Stop
            key={n.id}
            node={n}
            status={st(n)}
            stars={nodeStars(n, progress)}
            attempted={challengeAttempted(n, progress)}
            picture={n.kind === 'word' ? media.get(n.char)?.media : undefined}
            x={p.x}
            y={p.y}
            onGo={() => onGo(n)}
          />
        );
      })}

      {ticket && <FilmTicket info={ticket} x={690} y={108} onOpen={onTicket} />}

      {shizi && (
        <g
          transform={`translate(${(shizi.x - SHIZI_SIZE / 2).toFixed(1)} ${(shizi.y - SHIZI_SIZE / 2).toFixed(1)})`}
          aria-hidden
          style={{ pointerEvents: 'none' }}
        >
          <Shizi size={SHIZI_SIZE} pose="idle" />
        </g>
      )}
    </svg>
  );
});

/**
 * The adventure: islands in a row, one per word set, scrolled sideways. Self-directed: the child
 * only ever sees the next stop to tap, and Shizi stands on it.
 */
export function Path() {
  const profile = useProfile();
  const nav = useNavigate();
  const { mode: modeParam, after = null } = useSearch({ from: '/path' });
  // The layout being played: the URL wins, else the remembered choice. Remembered on every
  // visit so Home and "back to the path" return to the same islands.
  const mode: PathMode = modeParam ?? getAdventureMode();
  useEffect(() => {
    setAdventureMode(mode);
  }, [mode]);
  const [index, setIndex] = useState<ContentIndex | null>(null);
  const [progress, setProgress] = useState<ProgressMap | null>(null);
  const currentRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const advanced = useRef(false);
  const geo = useMemo(() => measureTrail(), []);

  useEffect(() => {
    void loadIndex().then(setIndex);
  }, []);
  useEffect(() => {
    if (!profile) return;
    void loadProgress(profile.id).then(setProgress);
  }, [profile]);

  const sections = useMemo(
    () => (index && profile ? buildPath(index, profile.levelId, profile.higherChinese, mode) : []),
    [index, profile, mode],
  );
  // The unlock frontier: the profile's lesson and everything before it is playable.
  const unlockedTo = index && profile ? unlockedOrder(index, profile.currentLessonId) : 1;
  const statuses = useMemo(
    () =>
      profile && progress
        ? pathStatuses(sections, progress, unlockedTo, after)
        : new Map<string, NodeStatus>(),
    [profile, progress, sections, unlockedTo, after],
  );
  const currentId = currentNode(statuses);
  const currentLesson = useMemo(
    () => sections.find((s) => s.nodes.some((n) => n.id === currentId))?.lesson ?? null,
    [sections, currentId],
  );

  // Lessons beyond the frontier are locked and fold to one misted island each. An unlocked
  // lesson ahead of the next-up stop with nothing done yet folds too: the trail ahead is a
  // promise, not a row of empty islands, and a tap (or its chip below) opens it.
  // Remembered for the tab session so coming back from a stop finds the lesson still open.
  const openedKey = `ezishizi:opened:${profile?.id ?? ''}`;
  const [opened, setOpened] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    // The profile can arrive after the first render, so restore once its key is known.
    if (!profile) return;
    try {
      const stored = JSON.parse(sessionStorage.getItem(openedKey) ?? '[]') as string[];
      if (stored.length) setOpened((prev) => new Set([...prev, ...stored]));
    } catch {
      /* nothing remembered */
    }
  }, [profile, openedKey]);
  const openLesson = useCallback(
    (id: string) =>
      setOpened((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev).add(id);
        try {
          sessionStorage.setItem(openedKey, JSON.stringify([...next]));
        } catch {
          /* private mode: this visit only */
        }
        return next;
      }),
    [openedKey],
  );
  const locked = useMemo(() => {
    if (!progress) return new Set<string>();
    return new Set(
      sections.filter((s) => !lessonUnlocked(s, progress, unlockedTo)).map((s) => s.lesson.id),
    );
  }, [sections, progress, unlockedTo]);
  const folded = useMemo(() => {
    if (!profile || !progress) return new Set<string>();
    const aheadOf = currentLesson?.order ?? unlockedTo;
    return new Set(
      sections
        .filter(
          (s) =>
            locked.has(s.lesson.id) ||
            (s.lesson.order > aheadOf &&
              !opened.has(s.lesson.id) &&
              !s.nodes.some((n) => nodeDone(n, progress))),
        )
        .map((s) => s.lesson.id),
    );
  }, [sections, profile, progress, opened, locked, currentLesson, unlockedTo]);
  useEffect(() => {
    currentRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [currentId, geo]);

  // Quick switching: the chips under the header jump to a lesson's first island (opening it if
  // it was folded), and the chip for the lesson nearest the middle of the screen is marked. The
  // set pills do the same one level down, jumping to a single island within the lesson in view.
  const [viewing, setViewing] = useState<string | null>(null);
  const [viewingSet, setViewingSet] = useState<string | null>(null);
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  // Tapping a stop navigates straight to /play/<id>, which only then fetches and parses the
  // lesson bundle — a fetch plus a parse sitting on the critical path of the game appearing.
  // So warm the cache ahead of the tap: once the lesson centred on screen settles (or, before
  // any scrolling, the profile's current lesson), fetch it during idle time. A short delay
  // means flicking past several islands doesn't fetch each one, only the one the child stops on.
  const prefetchTarget = viewing ?? currentLesson?.id ?? null;
  useEffect(() => {
    if (!prefetchTarget || locked.has(prefetchTarget)) return;
    const fetchNow = () => loadLesson(prefetchTarget).catch(() => {});
    let idleHandle:
      ReturnType<typeof requestIdleCallback> | ReturnType<typeof setTimeout> | undefined;
    const timer = setTimeout(() => {
      idleHandle =
        typeof requestIdleCallback === 'function'
          ? requestIdleCallback(fetchNow)
          : setTimeout(fetchNow, 0);
    }, 200);
    return () => {
      clearTimeout(timer);
      if (idleHandle === undefined) return;
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(idleHandle as number);
      else clearTimeout(idleHandle as ReturnType<typeof setTimeout>);
    };
  }, [prefetchTarget, locked]);
  // Whether there is another island to reach that way, for the two big arrows: judged from the
  // scroll position itself (not from what is centred) so the arrow only disappears once the
  // scroller has truly run out of road, `scroll-snap-type: proximity` leaves it a little slack
  // either side of the end islands.
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const lessonEl = useCallback(
    (id: string) =>
      scrollerRef.current?.querySelector<HTMLElement>(`[data-lesson="${id}"]`) ?? null,
    [],
  );
  const setEl = useCallback(
    (id: string) => scrollerRef.current?.querySelector<HTMLElement>(`[data-set="${id}"]`) ?? null,
    [],
  );
  // A tap names where the row is going before the smooth scroll gets there, so it marks the chip
  // straight away rather than waiting on the scroll's own `scroll` events, which a slow machine
  // can coalesce away entirely. And until the row arrives, the islands it sweeps past must not
  // take the mark back: on a starved main thread (CI caught one stalling ~2s mid-scroll) a
  // measure from the scroll's first frames, still sitting on the lesson being left, would win and
  // stand until the next measure, long after the row had settled. So the aim holds the mark until
  // a measure finds the target in the middle, the child scrolls by hand, or the scroll truly
  // settles (`scrollend`: a target the row can't centre; see AIM_IDLE_MS where there is none).
  const aim = useRef<{ lesson: string; set: string | null; timer: number } | null>(null);
  const remeasure = useRef<() => void>(() => {});
  const releaseAim = useCallback(() => {
    if (aim.current) clearTimeout(aim.current.timer);
    aim.current = null;
  }, []);
  const armAim = useCallback(() => {
    if (!aim.current || HAS_SCROLLEND) return;
    clearTimeout(aim.current.timer);
    aim.current.timer = window.setTimeout(() => {
      releaseAim();
      remeasure.current();
    }, AIM_IDLE_MS);
  }, [releaseAim]);
  // `el` is what the tap is about to centre, or null when it isn't on screen yet (a lesson still
  // unfolding). A tap that won't move the row holds nothing: no scroll means no measure and no
  // `scrollend` to let go, and a stale aim would freeze the marks through the next scroll. That
  // is judged from the geometry, not from waiting a frame to see whether a scroll started, which
  // a starved main thread can't answer.
  const holdAim = useCallback(
    (lesson: string, set: string | null, el: Element | null) => {
      releaseAim();
      setViewing(lesson);
      if (set) setViewingSet(set);
      const scroller = scrollerRef.current;
      if (el && scroller) {
        const box = scroller.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        const want = scroller.scrollLeft + (r.left + r.width / 2 - (box.left + box.width / 2));
        const to = Math.min(Math.max(want, 0), scroller.scrollWidth - scroller.clientWidth);
        if (Math.abs(to - scroller.scrollLeft) < 1) return;
      }
      aim.current = { lesson, set, timer: 0 };
      armAim();
    },
    [releaseAim, armAim],
  );
  useEffect(() => releaseAim, [releaseAim]);
  const scrollToLesson = useCallback(
    (id: string) => {
      // Aim at the lesson's first island, which is where the scroll below centres. A locked
      // lesson stays one folded island with no set of its own, so it is aimed at by lesson alone.
      const unfolding = folded.has(id) && !locked.has(id);
      holdAim(
        id,
        locked.has(id)
          ? null
          : (sections.find((s) => s.lesson.id === id)?.islands[0]?.set.id ?? null),
        unfolding ? null : lessonEl(id),
      );
      if (unfolding) {
        // Unfold first; the islands scroll into view once they are on screen.
        openLesson(id);
        setPendingScroll(id);
        return;
      }
      lessonEl(id)?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    },
    [folded, locked, openLesson, lessonEl, holdAim, sections],
  );
  const scrollToSet = useCallback(
    (id: string) => {
      // Same reasoning as scrollToLesson: hold both the set and its lesson as viewed up front.
      const owner = sections.find((s) => s.islands.some((isl) => isl.set.id === id));
      const el = setEl(id);
      // `data-lesson` and `data-set` sit on the same element, so a lookup by set id already
      // carries its lesson id too — a fallback for when `sections` hasn't caught up yet.
      const lesson = owner?.lesson.id ?? el?.dataset['lesson'];
      if (lesson) holdAim(lesson, id, el);
      else {
        setViewingSet(id);
        setViewing(null);
      }
      el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    },
    [sections, setEl, holdAim],
  );
  useEffect(() => {
    if (!pendingScroll) return;
    const el = lessonEl(pendingScroll);
    if (!el) return;
    el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    setPendingScroll(null);
  }, [pendingScroll, folded, lessonEl]);
  // The arrows step one island at a time: "next" is the nearest island whose centre sits to the
  // right of the scroller's own centre, "previous" the nearest one to the left. The few-pixel
  // tolerance keeps the island that is already centred from being picked as its own neighbour.
  const stepIsle = useCallback(
    (dir: 1 | -1) => {
      // The arrows sit beside the scroller, not in it, so the grab listeners never see them.
      releaseAim();
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const box = scroller.getBoundingClientRect();
      const mid = box.left + box.width / 2;
      const TOL = 8;
      let pick: { el: Element; center: number } | null = null;
      for (const el of scroller.querySelectorAll('.isle')) {
        const r = el.getBoundingClientRect();
        const center = r.left + r.width / 2;
        if (dir === 1 && center > mid + TOL) {
          if (!pick || center < pick.center) pick = { el, center };
        } else if (dir === -1 && center < mid - TOL) {
          if (!pick || center > pick.center) pick = { el, center };
        }
      }
      pick?.el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    },
    [releaseAim],
  );
  const goPrev = useCallback(() => stepIsle(-1), [stepIsle]);
  const goNext = useCallback(() => stepIsle(1), [stepIsle]);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const box = scroller.getBoundingClientRect();
      const mid = box.left + box.width / 2;
      let best: { id: string; d: number } | null = null;
      for (const el of scroller.querySelectorAll<HTMLElement>('[data-lesson]')) {
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - mid);
        if (!best || d < best.d) best = { id: el.dataset['lesson']!, d };
      }
      let bestSet: { id: string; d: number } | null = null;
      for (const el of scroller.querySelectorAll<HTMLElement>('[data-set]')) {
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - mid);
        if (!bestSet || d < bestSet.d) bestSet = { id: el.dataset['set']!, d };
      }
      const a = aim.current;
      if (a && best?.id === a.lesson && (!a.set || bestSet?.id === a.set)) releaseAim();
      if (!aim.current) {
        setViewing(best?.id ?? null);
        setViewingSet(bestSet?.id ?? null);
      }
      const EPS = 2;
      setCanPrev(scroller.scrollLeft > EPS);
      setCanNext(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - EPS);
    };
    const onScroll = () => {
      // Still moving, so an aimed scroll isn't stuck yet.
      armAim();
      if (!raf) raf = requestAnimationFrame(measure);
    };
    remeasure.current = onScroll;
    measure();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    // The child taking the row in hand overrides whatever a tap was aiming at.
    const onGrab = () => releaseAim();
    const grabs = ['pointerdown', 'wheel', 'touchstart', 'keydown'] as const;
    for (const type of grabs) scroller.addEventListener(type, onGrab, { passive: true });
    // `scroll` alone isn't enough on a slow machine: a smooth scroll that gets superseded or
    // coalesced can end without firing a final `scroll` event, leaving `viewing` stale (Chromium
    // and Firefox fire `scrollend` when the scroll truly settles, which is also what lets go of a
    // tap's aim; Safari without it still has the aim's own timer and the other listeners here).
    // And unfolding a lesson reflows the row without scrolling at all, so a `ResizeObserver` on
    // the scroller's content re-measures whenever the islands' own layout changes.
    const onScrollEnd = () => {
      releaseAim();
      onScroll();
    };
    scroller.addEventListener('scrollend', onScrollEnd, { passive: true });
    const ro = new ResizeObserver(onScroll);
    const content = scroller.firstElementChild;
    if (content) ro.observe(content);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('scrollend', onScrollEnd);
      for (const type of grabs) scroller.removeEventListener(type, onGrab);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [progress, index, geo, folded, armAim, releaseAim]);

  // The ambient scenery never stops (styles.css), so the whole row's worth of it runs whether or
  // not the child can see any of it: the Safari review counted 177 running animations on a fresh
  // Adventure page, 159 of them on elements outside the viewport, each also holding a persistent
  // `will-change` layer hint. This marks what is out of sight; the rule in styles.css pauses it
  // and withdraws the hint.
  //
  // Deliberately the cheap, reversible half of what that review suggested: no markup moves and
  // nothing unmounts, so Safari's own frame timeline can be compared with the offscreen scenery
  // paused and without it before anyone decides whether the row needs virtualising. Counting
  // animations does not prove what they cost.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const row = scroller?.firstElementChild;
    if (!scroller || !row) return;
    // The last answer per island, so returning to the tab restores exactly the right subset
    // rather than waiting for the observer to deliver a fresh batch.
    const seen = new Map<Element, boolean>();
    const apply = (el: Element, onScreen: boolean) => {
      if (onScreen && !document.hidden) el.removeAttribute('data-still');
      else el.setAttribute('data-still', 'true');
    };
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          seen.set(e.target, e.isIntersecting);
          apply(e.target, e.isIntersecting);
        }
      },
      { root: scroller, rootMargin: SCENERY_MARGIN },
    );
    // The row's own children rather than `.isle`: an unfolded island is a wrapper div and a
    // folded lesson is the svg itself, and this way the trailing "More lessons soon" mascot —
    // permanently off the right-hand end, permanently animating — is covered by the same switch.
    const watched = Array.from(row.children);
    for (const el of watched) io.observe(el);
    const onVisibility = () => {
      for (const [el, onScreen] of seen) apply(el, onScreen);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      io.disconnect();
      // Anything still marked would stay frozen with no observer left to thaw it.
      for (const el of watched) el.removeAttribute('data-still');
    };
    // `sections` is load-bearing here and is not in the sibling effect's list above: switching
    // between the Games/Skills/Words layouts replaces the row's DOM without touching progress,
    // index or folded, and an observer holds element references rather than re-querying.
  }, [sections, folded, geo, progress, index]);

  // Finishing every stop of the school lesson moves the child on, so Home and the study area
  // follow the path (a parent can still override from the dashboard).
  useEffect(() => {
    if (!profile || !progress || advanced.current) return;
    const nextLessonId = lessonToAdvanceTo(sections, progress, profile.currentLessonId);
    if (!nextLessonId) return;
    advanced.current = true;
    void persistLessonAdvance(profile.id, nextLessonId).catch(() => {
      advanced.current = false;
    });
  }, [profile, progress, sections]);

  // The stop's own navigation is shared with the result screen's "Next" button (lib/pathNav.ts),
  // so the two never open a stop two different ways. Kept stable (and above the early return
  // below, for the rules of hooks) so it doesn't itself force every island to re-render.
  // A chest (the reward disc of a non-story island) opens an overlay first; everything else goes
  // straight to what it leads to.
  const [chest, setChest] = useState<PathNode | null>(null);
  // A stop that offers two games asks which one first.
  const [choosing, setChoosing] = useState<PathNode | null>(null);
  const go = useCallback(
    (node: PathNode) => {
      if (node.kind === 'reward' && node.reward.type === 'game') setChest(node);
      else if (pendingChoice(node)) setChoosing(node);
      else goToNode(nav, node);
    },
    [nav],
  );
  // A sticker prize is saved the moment the chest opens, but only shown as done on the path once
  // the overlay closes: the path would otherwise scroll on to the next island behind it.
  const pendingFold = useRef<ReturnType<typeof markerSession> | null>(null);
  const closeChest = useCallback(() => {
    const rec = pendingFold.current;
    pendingFold.current = null;
    if (rec) setProgress((p) => (p ? foldSession(p, rec) : p));
    setChest(null);
  }, []);

  // Story films: which island carries each film's ticket, and the once-only "film unlocked" note.
  const films = useMemo(
    () => (index && profile ? buildFilms(index, profile.levelId) : []),
    [index, profile],
  );
  const tickets = useMemo(() => {
    const m = new Map<string, FilmTicketInfo>();
    if (!progress) return m;
    for (const s of sections) {
      const n = filmNumberForLesson(s.lesson.order);
      const film = n ? filmByNumber(films, n) : null;
      const last = s.islands[s.islands.length - 1];
      if (!film || film.unlockLessonId !== s.lesson.id || !last) continue;
      m.set(last.set.id, {
        n: film.n,
        unlocked: filmUnlocked(film, sections, progress),
        watched: filmWatched(film, progress),
      });
    }
    return m;
  }, [films, sections, progress]);
  const [celebrate, setCelebrate] = useState<Film | null>(null);
  useEffect(() => {
    if (!profile || !progress || celebrate || chest) return;
    const film = filmToCelebrate(films, sections, progress, seenFilms(profile.id));
    if (!film) return;
    markFilmSeen(profile.id, film.n);
    setCelebrate(film);
  }, [profile, progress, films, sections, celebrate, chest]);
  const openFilm = useCallback(
    (n: number) => void nav({ to: '/film/$n', params: { n: String(n) } }),
    [nav],
  );

  if (!profile) return null;

  const chestIsland = chest
    ? sections.flatMap((s) => s.islands).find((isl) => isl.set.id === chest.setId)
    : undefined;
  const chestPrizeNow = chestIsland
    ? chestPrize(profile.id, chestIsland.set.id, stickerChars(chestIsland.set))
    : null;

  const currentIslandId = (() => {
    for (const s of sections)
      for (const isl of s.islands) if (isl.nodes.some((n) => n.id === currentId)) return isl.set.id;
    return null;
  })();

  // One chip per unlocked lesson, then the first locked one so the child can see what is next.
  const firstLocked = sections.find((s) => locked.has(s.lesson.id))?.lesson.id ?? null;
  const chips = sections.filter((s) => !locked.has(s.lesson.id) || s.lesson.id === firstLocked);
  const chipState = (section: PathSection): NodeStatus => {
    if (locked.has(section.lesson.id)) return 'locked';
    if (section.lesson.id === currentLesson?.id) return 'current';
    if (section.nodes.every((n) => statuses.get(n.id) === 'done')) return 'done';
    return 'open';
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <header className="flex items-center justify-between px-6 py-3">
        <Link to="/home" className="btn btn-ghost btn-sm">
          ← Home
        </Link>
        <div className="flex items-center gap-3">
          <ModuleIcon id="path" className="h-8 w-8 text-brand" />
          <h1 className="display hidden text-3xl text-brand landscape:block">Adventure</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Games is the island. Skills (seven drill stops) and Words (one stop per word) are
              the layouts it replaced, kept so a profile part-way through one can finish it and
              so the two can still be compared; they are not offered to a new child. */}
          <div
            className="flex h-9 items-center rounded-full bg-white p-1 text-sm font-bold shadow-sm"
            role="group"
            aria-label="Island layout"
            data-testid="mode-switch"
          >
            {(['games', 'skills', 'words'] as const).map((m) => (
              <Link
                key={m}
                to="/path"
                search={{ mode: m }}
                replace
                className={`flex h-7 items-center rounded-full px-3 ${
                  mode === m ? 'bg-brand text-white' : 'text-muted'
                } ${m !== 'games' && mode !== m ? 'opacity-60' : ''}`}
                aria-current={mode === m ? 'true' : undefined}
                title={m === 'games' ? undefined : 'An earlier layout, kept for comparison'}
                data-testid={`mode-${m}`}
              >
                {m === 'games' ? 'Games' : m === 'skills' ? 'Skills' : 'Words'}
              </Link>
            ))}
          </div>
          <Link
            to="/stickers"
            className="btn btn-ghost btn-sm"
            aria-label="Sticker book"
            data-testid="go-stickers"
          >
            <StickerBookIcon className="h-6 w-6 text-brand" />
          </Link>
          <Link
            to="/films"
            className="btn btn-ghost btn-sm"
            aria-label="Cinema"
            data-testid="go-films"
          >
            <ClapperIcon className="h-6 w-6 text-brand" />
          </Link>
          <Link to="/study" className="btn btn-ghost btn-sm">
            Study
          </Link>
        </div>
      </header>

      {progress && index && geo && sections.length > 0 && (
        <nav
          className="flex items-center gap-2 px-6 pb-2"
          aria-label="Lessons"
          data-testid="lesson-switcher"
        >
          {currentLesson && (
            <button
              type="button"
              className="flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-white pr-3 pl-1 text-sm font-bold text-brand shadow-sm"
              data-testid="jump-current"
              onClick={() => {
                // The tap is the intent; the scroll catches up (see scrollToLesson).
                holdAim(currentLesson.id, currentIslandId, currentRef.current);
                currentRef.current?.scrollIntoView({
                  inline: 'center',
                  block: 'nearest',
                  behavior: 'smooth',
                });
              }}
            >
              <Shizi size={32} pose="idle" />
              Next up
            </button>
          )}
          {/* px-1.5 (matching the ring width) and py-1: the viewed chip draws ring-4, and
              overflow-x-auto clips to the padding box, so with no padding the ring on the first
              or last chip was being sliced off. */}
          <div className="chips flex min-w-0 flex-1 gap-1.5 overflow-x-auto px-1.5 py-1">
            {chips.map((section) => {
              const state = chipState(section);
              const id = section.lesson.id;
              const here = id === viewing;
              const title = section.lesson.title;
              // Only the lesson in view expands into set pills, so the row stays one lesson deep
              // at a time no matter how many sets it has.
              const showSets = here && !folded.has(id) && section.islands.length > 1;
              return (
                <div key={id} className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    className={`flex h-10 shrink-0 items-center gap-1 rounded-full px-3 text-base font-bold transition-shadow ${
                      state === 'current'
                        ? 'bg-brand text-white'
                        : state === 'done'
                          ? 'bg-ok text-white'
                          : state === 'locked'
                            ? 'bg-white/60 text-muted'
                            : 'bg-white text-ink'
                    } ${here ? 'ring-4 ring-accent' : 'shadow-sm'}`}
                    aria-label={`Lesson ${section.lesson.order}${title ? ` ${title.en}` : ''}${
                      state === 'locked'
                        ? ', locked'
                        : state === 'done'
                          ? ', finished'
                          : state === 'current'
                            ? ', next up'
                            : ''
                    }`}
                    aria-current={here ? 'true' : undefined}
                    title={title ? `${title.zh} · ${title.en}` : undefined}
                    data-testid={`lesson-chip-${id}`}
                    data-state={state}
                    onClick={() => scrollToLesson(id)}
                  >
                    {state === 'locked' ? (
                      <svg viewBox="-12 -12 24 24" className="h-4 w-4" aria-hidden>
                        <LockIcon fg="currentColor" />
                      </svg>
                    ) : state === 'done' ? (
                      <span aria-hidden>✓</span>
                    ) : null}
                    {section.lesson.order}
                  </button>
                  {showSets &&
                    section.islands.map((island) => {
                      const setId = island.set.id;
                      const setHere = setId === viewingSet;
                      return (
                        // Spelled out, and in the brand's soft fill rather than the chips' white:
                        // a bare number beside the lesson numbers read as another lesson, and the
                        // row said "1 1 2 2 3" with no way to tell the two kinds apart.
                        <button
                          key={setId}
                          type="button"
                          className={`flex h-8 shrink-0 items-center rounded-full bg-brand-soft px-2.5 text-sm font-bold text-brand ${
                            setHere ? 'ring-4 ring-accent' : ''
                          }`}
                          aria-label={`Lesson ${section.lesson.order} set ${island.set.order}`}
                          data-testid={`set-chip-${setId}`}
                          onClick={() => scrollToSet(setId)}
                        >
                          Set {island.set.order}
                        </button>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </nav>
      )}

      {!progress || !index || !geo ? (
        <p className="text-center text-muted">One moment…</p>
      ) : sections.length === 0 ? (
        <p className="text-center text-muted">No lessons are ready for this level yet.</p>
      ) : (
        // The scroller keeps its own classes and its flex-1 sizing; this wrapper only exists so
        // the two arrows have something to sit on top of without becoming part of the scrolling
        // content themselves.
        <div className="relative flex flex-1 min-h-0">
          <div
            ref={scrollerRef}
            className="isles flex-1 overflow-x-auto overflow-y-hidden"
            data-testid="islands"
          >
            <div className="flex h-full items-center px-6">
              {sections.map((section: PathSection) =>
                folded.has(section.lesson.id) ? (
                  <FoldedIsland
                    key={section.lesson.id}
                    lesson={section.lesson}
                    geo={geo}
                    locked={locked.has(section.lesson.id)}
                    onOpen={openLesson}
                  />
                ) : (
                  section.islands.map((island, i) => (
                    <div
                      key={island.set.id}
                      ref={island.set.id === currentIslandId ? currentRef : undefined}
                      className="flex h-full items-center"
                      data-lesson={section.lesson.id}
                      data-set={island.set.id}
                    >
                      <IslandView
                        island={island}
                        statuses={statuses}
                        progress={progress}
                        geo={geo}
                        first={i === 0}
                        onGo={go}
                        ticket={tickets.get(island.set.id)}
                        onTicket={openFilm}
                      />
                    </div>
                  ))
                ),
              )}
              <div className="mx-10 flex flex-col items-center gap-2 text-muted">
                <Shizi size={80} pose="think" />
                <span className="whitespace-nowrap">More lessons soon…</span>
              </div>
            </div>
          </div>
          {/* Pinned over the scroller rather than inline with it, and pointer-events-none except
              for the buttons themselves, so the island underneath stays fully tappable. */}
          <div className="pointer-events-none absolute inset-0">
            {canPrev && <IsleArrow dir="prev" onGo={goPrev} />}
            {canNext && <IsleArrow dir="next" onGo={goNext} />}
          </div>
        </div>
      )}

      {chest && chestIsland && chestPrizeNow && progress && (
        <ChestOverlay
          lessonId={chest.lessonId}
          setId={chest.setId}
          prize={chestPrizeNow}
          done={nodeDone(chest, progress)}
          media={
            chestPrizeNow.type === 'sticker'
              ? chestIsland.set.pictures.find((p) => p.char === chestPrizeNow.char)?.media
              : undefined
          }
          onOpened={() => {
            const rec = markerSession(profile.id, chest.lessonId, chest.setId, 'game');
            pendingFold.current = rec;
            saveMarkerInBackground(rec);
          }}
          onPlay={() => {
            const node = chest;
            closeChest();
            goToNode(nav, node);
          }}
          onBook={() => {
            pendingFold.current = null;
            void nav({ to: '/stickers' });
          }}
          onClose={closeChest}
        />
      )}

      {choosing && pendingChoice(choosing) && (
        <GameChooser
          games={pendingChoice(choosing)!}
          onPick={(game) => {
            const node = choosing;
            setChoosing(null);
            goToNode(nav, node, game);
          }}
          onClose={() => setChoosing(null)}
        />
      )}

      {celebrate && !chest && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center bg-ink/55 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Film unlocked"
          data-testid="film-unlocked"
        >
          <div className="pop relative flex w-full max-w-md flex-col items-center gap-2 overflow-hidden rounded-[32px] bg-paper p-6 text-center shadow-xl">
            <Confetti count={20} />
            <Shizi size={120} pose="cheer" />
            <div className="display text-3xl text-brand">A new film is ready!</div>
            <div className="text-lg text-muted">Film {celebrate.n} is waiting in the Cinema.</div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="film-later"
                onClick={() => setCelebrate(null)}
              >
                Later
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-testid="film-now"
                onClick={() => openFilm(celebrate.n)}
              >
                Watch now ▶
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phones: the islands want width. In portrait, ask for a turn instead of squeezing. */}
      <div className="fixed inset-0 z-20 hidden flex-col items-center justify-center gap-4 bg-paper/95 text-center max-sm:portrait:flex">
        <Shizi size={120} pose="think" />
        <p className="display text-2xl text-brand">Turn your phone sideways</p>
        <p className="text-muted">The islands need the wide way round.</p>
      </div>
    </div>
  );
}
